import React, { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, X } from 'lucide-react';
import { Button } from './ui/button';
import 'react-day-picker/dist/style.css';

function ModernDateRangePicker({ dateRange, onDateRangeChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useState({
    from: dateRange?.from || new Date(),
    to: dateRange?.to || new Date()
  });

  const handleSelect = (selectedRange) => {
    if (selectedRange?.from) {
      setRange(selectedRange);
      // Ne plus fermer automatiquement - l'utilisateur cliquera sur "Appliquer"
    }
  };

  const handleApply = () => {
    if (range?.from && range?.to) {
      onDateRangeChange(range);
      setIsOpen(false);
    }
  };

  const handleReset = () => {
    // Vraie réinitialisation - efface complètement la sélection
    setRange({ from: undefined, to: undefined });
  };

  const formatDateDisplay = () => {
    if (!dateRange?.from) return 'Sélectionner une période';
    
    const fromStr = format(dateRange.from, 'dd MMM', { locale: fr });
    const toStr = dateRange.to ? format(dateRange.to, 'dd MMM yyyy', { locale: fr }) : '';
    
    return dateRange.to ? `${fromStr} - ${toStr}` : fromStr;
  };

  return (
    <div className="relative w-full">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left focus:outline-none"
      >
        <span className="text-gray-700 font-semibold">
          {formatDateDisplay()}
        </span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 animate-in fade-in slide-in-from-top-2 duration-200 min-w-[420px]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-bold text-gray-800">Sélectionner une période</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <DayPicker
              mode="range"
              selected={range}
              onSelect={handleSelect}
              locale={fr}
              weekStartsOn={1}
              numberOfMonths={1}
              className="modern-date-picker"
              classNames={{
                months: "flex flex-col",
                month: "space-y-4",
                caption: "flex justify-center items-center mb-8 relative px-2",
                caption_label: "text-2xl font-bold text-gray-900 lowercase",
                nav: "flex items-center gap-32 absolute w-full justify-between px-4",
                nav_button: "h-10 w-10 bg-transparent p-0 hover:bg-gray-50 rounded-lg flex items-center justify-center",
                nav_button_previous: "absolute left-0",
                nav_button_next: "absolute right-0",
                table: "w-full border-collapse mt-6",
                head_row: "flex justify-between mb-4",
                head_cell: "text-gray-400 w-14 font-medium text-lg uppercase",
                row: "flex justify-between w-full mt-3",
                cell: "text-center text-lg p-0 relative w-14",
                day: "h-14 w-14 p-0 font-semibold text-gray-600 hover:bg-gray-50 rounded-2xl transition-colors flex items-center justify-center",
                day_selected: "bg-emerald-500 text-white hover:bg-emerald-600 rounded-2xl font-bold shadow-sm",
                day_today: "text-gray-900 font-bold",
                day_outside: "text-gray-300 opacity-50",
                day_disabled: "text-gray-300 opacity-30",
                day_range_middle: "bg-emerald-100 text-emerald-900 rounded-2xl font-semibold",
                day_range_start: "bg-emerald-500 text-white rounded-2xl font-bold shadow-md",
                day_range_end: "bg-emerald-500 text-white rounded-2xl font-bold shadow-md",
                day_hidden: "invisible"
              }}
            />

            {/* Instruction dynamique */}
            <div className="mt-6 text-center">
              <p className="text-base text-gray-500">
                {!range?.from && "Sélectionnez la date de début"}
                {range?.from && !range?.to && "Sélectionnez la date de fin"}
                {range?.from && range?.to && (
                  <span className="font-semibold text-emerald-600">
                    {format(range.from, "dd MMM", { locale: fr })} - {format(range.to, "dd MMM yyyy", { locale: fr })}
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                Réinitialiser
              </Button>
              <div className="flex items-center gap-4">
                <div className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                  {range.from && range.to ? (
                    <span className="font-medium">
                      {Math.ceil((range.to - range.from) / (1000 * 60 * 60 * 24)) + 1} jours sélectionnés
                    </span>
                  ) : (
                    <span className="italic">
                      Cliquez sur une date de début puis une date de fin
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={handleApply}
                  disabled={!range.from || !range.to}
                  className="bg-gradient-to-r from-[#004d3d] to-[#00ff7f] hover:from-[#003d30] hover:to-[#00e070] text-white disabled:bg-gray-300 disabled:cursor-not-allowed px-6"
                >
                  Appliquer
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      <style jsx="true">{`
        .modern-date-picker {
          font-family: inherit;
        }
      `}</style>
    </div>
  );
}

export default ModernDateRangePicker;
